import { alpha } from "@mui/material/styles";

import { format, intervalToDuration, isAfter, startOfDay } from "date-fns";

import {
  EVENT_TYPE_APPOINTMENT,
  EVENT_TYPE_APPOINTMENT_BLOCK
} from "constants/appointments";
import { Appointment } from "services/types/appointments.type";
import { Clinic } from "services/types/clinics.type";
import {
  AppointmentBlock,
  ClinicEvent,
  Events,
  PersonalEvent
} from "services/types/events.type";
import { WeeklySchedule } from "services/types/weekly-schedule.type";

import { SelectedStaffMember } from "../scheduler-side-sheet/filters/staff-filter/staff-filter.component";
import { STAFF_COLORS } from "../scheduler-side-sheet/filters/staff-filter/staff-search.component";

export const getAppointmentDuration = (
  startDate: Date | number | string | undefined | null,
  endDate: Date | number | string | undefined | null
) => {
  const start = new Date(startDate || "");
  const end = new Date(endDate || "");

  if (end < start) return 0;

  const interval = intervalToDuration({
    start,
    end
  });

  return Number(interval?.hours) * 60 + Number(interval?.minutes);
};

export const getStaffColor = (param: string | number) => {
  const color =
    typeof param === "number"
      ? STAFF_COLORS[Number(param) % STAFF_COLORS.length][400]
      : param;
  return {
    color,
    backgroundColor: alpha(color, 0.1),
    hoverBackgroundColor: alpha(color, 0.3),
    focusBackgroundColor: alpha(color, 0.4)
  };
};

export const findColorByGroupId = (
  STAFF: {
    [key: number]: SelectedStaffMember & { isOnlyDoctor: boolean };
  },
  id?: number
) =>
  id
    ? {
        color: STAFF[id]?.color,
        backgroundColor: STAFF[id]?.backgroundColor,
        hoverBackgroundColor: STAFF[id]?.hoverBackgroundColor,
        focusBackgroundColor: STAFF[id]?.focusBackgroundColor
      }
    : null;

const MAX_Z_INDEX = 98;
type Event = Appointment | PersonalEvent | ClinicEvent | AppointmentBlock;

export function sortAppointmentsByStartDate(appointments: Event[]) {
  return appointments.sort((a, b) => {
    const aStartDate = new Date(a.startDate).getTime();
    const bStartDate = new Date(b.startDate).getTime();
    const aEndDate = new Date(a.endDate).getTime();
    const bEndDate = new Date(b.endDate).getTime();
    return aStartDate - bStartDate || bEndDate - aEndDate;
  });
}

export function findOverlappedAppointments(
  appointments: Event[],
  index: number,
  appointment: Event
) {
  return appointments
    .slice(0, index)
    .filter(
      (prevAppointment) =>
        new Date(prevAppointment.endDate).getTime() >
        new Date(appointment.startDate).getTime()
    );
}

export function calculateZIndex(
  overlappedAppointment: Event | undefined,
  appointment: Event,
  zIndexMap: Map<number | string, number>
) {
  return overlappedAppointment?.id && zIndexMap.get(overlappedAppointment.id)
    ? zIndexMap.get(overlappedAppointment.id)! + 1
    : getEventZIndex(
        String(appointment?.type),
        String(appointment?.startDate),
        String(appointment?.endDate)
      );
}

export const getEventZIndex = (
  type: string,
  startDate: string,
  endDate: string
) => {
  if (type === EVENT_TYPE_APPOINTMENT_BLOCK) return MAX_Z_INDEX;

  const duration = getAppointmentDuration(startDate, endDate);
  const divider = type === EVENT_TYPE_APPOINTMENT ? 20 : 10;
  const zIndex = MAX_Z_INDEX - Math.round(duration / divider);

  return zIndex >= 1 ? zIndex : 1;
};

export const getMaxAppointmentZIndex = (appointments: Event[] = []): number =>
  Math.max(MAX_Z_INDEX, ...appointments.map((app) => app.zIndex ?? 0));

export const filterClinicsFromEvents = (
  appointments: (Appointment | PersonalEvent | AppointmentBlock)[] | undefined,
  clinics: Clinic[] | undefined,
  unique: boolean = true
) => {
  if (!unique) return clinics;

  const eventClinicIds =
    new Set<Number>(
      appointments?.map((event) => event.clinic?.id ?? event.clinicId)
    ) ?? [];

  return clinics?.filter((clinic) => eventClinicIds.has(clinic.id)) ?? [];
};

export function getClinicIdsFromEvents(
  events: Events | undefined,
  allClinics: Clinic[] | undefined,
  currentClinicId: number | undefined,
  isShowByClinicView: boolean,
  showAllClinics: boolean
): number[] {
  const clinicIds = new Set<number>();

  if (showAllClinics) {
    allClinics?.forEach(({ id }) => clinicIds.add(id));
    if (clinicIds.size === 0 && currentClinicId) {
      clinicIds.add(currentClinicId);
    }
  } else if (isShowByClinicView) {
    events?.appointments?.forEach(({ clinic }) => clinicIds.add(clinic.id));
    events?.personalEvents?.forEach(({ clinic }) => clinicIds.add(clinic.id));
    events?.appointmentBlocks?.forEach(({ clinic }) =>
      clinicIds.add(clinic.id)
    );
    if (clinicIds.size === 0 && currentClinicId) {
      clinicIds.add(currentClinicId);
    }
  } else {
    allClinics?.forEach(({ id }) => clinicIds.add(id));
  }

  return Array.from(clinicIds);
}

export const getRoundedTime = () => {
  const now = new Date();

  const hours = now.getHours();
  const minutes = now.getMinutes();

  const roundedMinutes = minutes < 30 ? 0 : 30;
  const roundedDate = new Date(
    new Date(now.getTime()).setMinutes(roundedMinutes)
  );

  const formattedTime = format(roundedDate.setHours(hours), "h:mm a");
  return formattedTime;
};

export const scrollScheduleToTime = () => {
  const time = getRoundedTime();
  const container = document.querySelector(
    ".Container-container .MainLayout-container"
  );
  const elements = Array.from(
    document.querySelectorAll(".Layout-timeScaleContainer span.Label-text")
  );

  const targetElement = elements.find((element) => {
    const content = element.textContent?.trim();
    return content === time || content === time.replace(" ", "");
  }) as HTMLElement;

  if (container && targetElement) {
    // Calculate scroll position relative to container
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();

    // Calculate the target position relative to the container
    const targetPosition =
      targetRect.top - containerRect.top + container.scrollTop;

    // Scroll to position with some offset from the top for better visibility
    const scrollOffset = 100; // Show some content above the target time

    container.scrollTo({
      top: Math.max(0, targetPosition - scrollOffset),
      behavior: "smooth"
    });
  } else {
    // Fallback: scroll to a reasonable default position if target element not found
    // This prevents the scheduler from staying at the bottom
    if (container) {
      const containerHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;

      // Scroll to roughly 1/3 from the top to show morning appointments
      const fallbackPosition = Math.min(
        scrollHeight * 0.33,
        scrollHeight - containerHeight
      );

      container.scrollTo({
        top: fallbackPosition,
        behavior: "smooth"
      });
    }
  }
};

export const getIsAppointmentDateInTheFuture = (
  appointmentStartDate: string | number | Date
): boolean => {
  const appointmentDate = new Date(appointmentStartDate);
  const now = new Date();

  return isAfter(startOfDay(appointmentDate), startOfDay(now));
};

export const DEFAULT_DAY_START_HOUR = 7;
export const DEFAULT_DAY_END_HOUR = 22;

const WEEKDAY_INDEX_TO_PREFIX = [
  "sun",
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat"
] as const;
const WORK_WEEK_PREFIXES = ["mon", "tue", "wed", "thu", "fri"] as const;
const ALL_WEEK_PREFIXES = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun"
] as const;

export const parseScheduleTimeToHours = (
  value?: string | null
): number | null => {
  if (!value) return null;
  const [rawHours, rawMinutes = "0"] = value.split(":");
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours + minutes / 60;
};

const getWeekdayPrefixFromDateString = (dateString: string): string | null => {
  const [year, month, day] = dateString.split("-").map(Number);
  if (!year || !month || !day) return null;
  return WEEKDAY_INDEX_TO_PREFIX[new Date(year, month - 1, day).getDay()];
};

const getVisibleWeekdayPrefixes = (
  currentViewName: string,
  selectedDate: string
): readonly string[] => {
  if (currentViewName === "Day") {
    const prefix = getWeekdayPrefixFromDateString(selectedDate);
    return prefix ? [prefix] : ALL_WEEK_PREFIXES;
  }
  if (currentViewName === "Work Week") return WORK_WEEK_PREFIXES;
  return ALL_WEEK_PREFIXES;
};

const getDateHours = (value?: Date | string | number | null): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getHours() + date.getMinutes() / 60;
};

type DayBoundsAppointment = {
  startDate?: Date | string | number | null;
  endDate?: Date | string | number | null;
};

export const computeSchedulerDayBounds = ({
  enabled,
  schedules,
  currentViewName,
  selectedDate,
  appointments = [],
  workShifts = []
}: {
  enabled: boolean;
  schedules: WeeklySchedule[];
  currentViewName: string;
  selectedDate: string;
  appointments?: DayBoundsAppointment[];
  workShifts?: DayBoundsAppointment[];
}): { startDayHour: number; endDayHour: number } => {
  const fallback = {
    startDayHour: DEFAULT_DAY_START_HOUR,
    endDayHour: DEFAULT_DAY_END_HOUR
  };
  if (!enabled || !schedules.length) return fallback;

  const prefixes = getVisibleWeekdayPrefixes(currentViewName, selectedDate);

  let start: number | null = null;
  let end: number | null = null;

  schedules.forEach((schedule) => {
    prefixes.forEach((prefix) => {
      const dayStart = parseScheduleTimeToHours(
        schedule[`${prefix}_start` as keyof WeeklySchedule]
      );
      const dayEnd = parseScheduleTimeToHours(
        schedule[`${prefix}_end` as keyof WeeklySchedule]
      );
      if (dayStart === null || dayEnd === null || dayEnd <= dayStart) return;
      start = start === null ? dayStart : Math.min(start, dayStart);
      end = end === null ? dayEnd : Math.max(end, dayEnd);
    });
  });

  // No usable clinic hours for the visible day(s): keep current behavior.
  if (start === null || end === null) return fallback;

  // Never clip appointments or work shifts: widen the window to include any event in view.
  [...appointments, ...workShifts].forEach((event) => {
    const eventStart = getDateHours(event.startDate);
    const eventEnd = getDateHours(event.endDate);
    if (eventStart !== null) {
      start = Math.min(start as number, eventStart);
    }
    if (eventEnd !== null && eventEnd > (eventStart ?? eventEnd)) {
      end = Math.max(end as number, eventEnd);
    }
  });

  const startDayHour = Math.min(Math.max(Math.floor(start as number), 0), 23);
  const endDayHour = Math.min(
    Math.max(Math.ceil(end as number), startDayHour + 1),
    24
  );

  return { startDayHour, endDayHour };
};
